// Small date/time formatting helpers shared across pages.
// Mirrors the local helpers first written in pages/admin/AdminTeam.tsx.
import i18n from "@/i18n";

// Locale ativo do i18n (ex.: "pt-BR") para o Intl formatar datas no idioma do
// usuário; cai em "en-US" antes do i18n inicializar.
function activeLocale(): string {
  return i18n.language || "en-US";
}

/** Coarse "x ago" label for an ISO timestamp. Empty input → "never".
 *  Rótulos passam pelo i18n para acompanhar o idioma do usuário.
 *
 *  Relativo até 7 dias, absoluto depois. O corte existe porque rótulo relativo
 *  grosso deixa de identificar a data: a faixa anterior ia a 30 dias e então
 *  caía em `N mo ago`, então três projetos de datas distintas (Jul 6, Jul 8,
 *  Jul 10) apareciam com o mesmo rótulo na coluna `UPDATED` de `/projects` —
 *  impossível ordenar visualmente. Passando de 7 dias, a data exata informa
 *  mais que a distância. Use `formatDateTime` no `title` de quem chama, para
 *  o timestamp completo ficar disponível no hover nas duas faixas. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return i18n.t("never");
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return i18n.t("just now");
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${i18n.t("min ago")}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${i18n.t("h ago")}`;
  const days = Math.floor(hours / 24);
  if (days === 1) return i18n.t("yesterday");
  if (days <= 7) return `${days} ${i18n.t("d ago")}`;
  return formatDate(iso);
}

/** Locale short date, e.g. "Jul 22, 2026". Empty/invalid → "—". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(activeLocale(), { month: "short", day: "2-digit", year: "numeric" });
}

/**
 * Rótulo curto de dia para eixo de gráfico, e.g. "Sep 14" — sem ano.
 *
 * Espera uma data-only `"YYYY-MM-DD"` (o formato de `CostByDay.day`, truncada
 * em UTC pelo backend) e formata em **UTC explícito**: sem isso, quem estiver
 * a oeste de UTC veria o dia anterior — `new Date("2026-09-14")` é meia-noite
 * UTC, que em `America/Sao_Paulo` (-03:00) já é 21h do dia 13.
 */
export function formatDayShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(activeLocale(), { month: "short", day: "2-digit", timeZone: "UTC" });
}

/**
 * Data pura `YYYY-MM-DD` formatada em **UTC explícito**, e.g. "Jan 01, 2013".
 *
 * Existe separada de `formatDate` pelo mesmo motivo do `formatDayShort`:
 * `new Date("2013-01-01")` é meia-noite UTC, que em `America/Sao_Paulo`
 * (-03:00) já é 21h de 31/12/2012. Formatar no fuso local troca o ano do
 * objeto na cara de quem lê, e `created_on` da TADIR é data pura.
 */
export function formatDateOnly(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(activeLocale(), {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * O ano de uma data pura, em UTC. `null` quando não há data.
 *
 * Existe para tabela: o formato longo do locale ("02 de abr. de 2016") quebra
 * em duas linhas numa coluna estreita e desalinha as demais. Ao lado da idade
 * em anos, o ano sozinho é o que confirma o número — a data completa fica no
 * painel de detalhe, que tem largura para ela.
 */
export function yearOfDateOnly(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.getUTCFullYear();
}

/**
 * Anos completos entre uma data pura e `now`. `null` quando não há data.
 *
 * Conta pelas partes em UTC, e não por divisão de milissegundos: dividir por
 * 365,25 dias erra na vizinhança do aniversário, que é justamente onde a
 * pergunta "quantos anos tem este Z" é feita.
 */
export function yearsSince(
  iso: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  let years = now.getUTCFullYear() - d.getUTCFullYear();
  const beforeAnniversary =
    now.getUTCMonth() < d.getUTCMonth() ||
    (now.getUTCMonth() === d.getUTCMonth() && now.getUTCDate() < d.getUTCDate());
  if (beforeAnniversary) years -= 1;
  return years < 0 ? null : years;
}

/** Full date + time, e.g. "Jul 22, 2026, 10:56". Empty/invalid → "—". */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(activeLocale(), {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Só a hora, e.g. "10:56". Empty/invalid → "—". */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString(activeLocale(), { hour: "2-digit", minute: "2-digit" });
}

/** Human byte size, e.g. 14.2 MB. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}
